import React from 'react'
// libs
import { useSnackbar } from 'notistack'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
// types
import { NGenerateUploadUrl } from 'types/upload'
// graphql
import { GENERATE_UPLOAD_URL_MUTATION, GENERATE_UPLOAD_URL_PUBLIC_MUTATION } from 'graphql/assets/mutations'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
// utils
import { handleError } from 'utils/errors/handlers'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'

const ABORT_ERROR = 'AbortError'

export const isAbortError = (error: unknown) => error instanceof DOMException && error.name === ABORT_ERROR

const createAbortError = () => new DOMException('Upload cancelled', ABORT_ERROR)

export interface UploadOptions {
  onProgress?: (percent: number) => void
  signal?: AbortSignal
}

const postForm = (url: string, body: FormData, { onProgress, signal }: UploadOptions = {}): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError())
      return
    }

    const xhr = new XMLHttpRequest()

    xhr.open('POST', url)

    // the listener is attached only when needed: it turns the request into a CORS-preflighted one
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100))
        }
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(xhr.responseText || 'Upload failed'))
      }
    }

    xhr.onerror = () => reject(new Error('Upload failed'))
    xhr.onabort = () => reject(createAbortError())

    signal?.addEventListener('abort', () => xhr.abort(), { once: true })

    xhr.send(body)
  })

const waitUntilAvailable = async (
  url: string,
  signal?: AbortSignal,
  maxAttempts = 20,
  intervalMs = 1500
): Promise<void> => {
  for (let i = 0; i < maxAttempts; i++) {
    if (signal?.aborted) {
      throw createAbortError()
    }

    let isAvailable = false

    try {
      const res = await fetch(url, { method: 'HEAD', redirect: 'manual', signal })
      // 2xx = proxy returned directly, opaqueredirect = file passed scan (redirect to S3, browser fetch with redirect:'manual' returns status 0)
      // 425 Too Early = file is still being scanned, keep polling
      isAvailable = res.ok || res.type === 'opaqueredirect'
    } catch {
      // network error — keep trying
    }

    // a cancel during the request must win over a successful response
    if (signal?.aborted) {
      throw createAbortError()
    }

    if (isAvailable) {
      return
    }

    if (i < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs))
    }
  }
  throw new Error('File is not available after upload (firewall check timeout)')
}

export const useFileUpload = () => {
  const isPublic = React.useContext(PublicContext)

  const { client } = useApolloClient()

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const [uploading, setUploading] = React.useState(false)

  const upload = async (
    file: File | Blob,
    variables: NGenerateUploadUrl.IVariables,
    options: UploadOptions = {}
  ): Promise<string> => {
    setUploading(true)
    try {
      if (!variables?.file?.contentType) {
        throw new Error('Missing contentType')
      }

      const {
        data: {
          [isPublic ? 'generateUploadUrlsPublic' : 'generateUploadUrls']: { downloadUrl, uploadUrl, uploadFormData }
        }
      } = await client.mutate<NGenerateUploadUrl.IResponse>({
        mutation: isPublic ? GENERATE_UPLOAD_URL_PUBLIC_MUTATION : GENERATE_UPLOAD_URL_MUTATION,
        variables
      })

      const form = new FormData()

      Object.entries(uploadFormData).forEach(([key, value]) => {
        form.append(key, value as string)
      })

      form.append('file', file)

      await postForm(uploadUrl, form, options)

      await waitUntilAvailable(downloadUrl, options.signal)

      return downloadUrl
    } catch (error) {
      if (isAbortError(error)) {
        throw error
      }

      handleError(error, 'useFileUpload.upload')

      enqueueSnackbar(error instanceof Error ? error.message : 'Upload failed', {
        variant: 'error',
        autoHideDuration: null,
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })

      throw error
    } finally {
      setUploading(false)
    }
  }

  return { upload, uploading }
}
